import dayjs from "dayjs";
import { get } from "lodash";
import { AUTHORITY_TYPE, NIR_INHERITANCE_FROM } from "utils/constants";

export const getAgeFromBirthDate = (birthDate) => {
  if (!birthDate) return 0;
  return dayjs().diff(dayjs(birthDate), "year");
};

/** NIR parent source for BOTH is stored on child identity contact `fields`. */
export const getNirInheritanceFromStored = (person) =>
  get(person, "identity.fields.nirInheritanceFrom");

/**
 * Defaults authority for minors (undefined → FATHER). Preserves SELF.
 * For BOTH, defaults identity.fields.nirInheritanceFrom → ADHERENT.
 */
export const normalizeMiltisChildRelatedPerson = (person) => {
  if (!person?.identity) return person;
  const user = person.identity?.user ?? {};
  const age = getAgeFromBirthDate(user.birthDate);
  const backendAuthority = person.authority;

  const authority =
    age > 18
      ? (backendAuthority ?? null)
      : backendAuthority === undefined
        ? AUTHORITY_TYPE.FATHER
        : backendAuthority;

  const fields = { ...(person.identity.fields || {}) };

  if (authority === AUTHORITY_TYPE.BOTH) {
    const current = fields.nirInheritanceFrom;
    if (
      current !== NIR_INHERITANCE_FROM.SPOUSE &&
      current !== NIR_INHERITANCE_FROM.ADHERENT
    ) {
      fields.nirInheritanceFrom = NIR_INHERITANCE_FROM.ADHERENT;
    }
  } else {
    delete fields.nirInheritanceFrom;
  }

  return {
    ...person,
    authority,
    identity: {
      ...person.identity,
      fields,
    },
  };
};

export const resolveChildInheritedNir = ({
  authority,
  nirInheritanceFrom,
  adherentNir,
  spouseNir,
  hasSpouse = false,
}) => {
  if (authority === AUTHORITY_TYPE.SELF) {
    return "";
  }
  if (authority === AUTHORITY_TYPE.FATHER) {
    return adherentNir ?? "";
  }
  if (authority === AUTHORITY_TYPE.MOTHER && hasSpouse) {
    return spouseNir ?? "";
  }
  if (authority === AUTHORITY_TYPE.BOTH) {
    return nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE
      ? (spouseNir ?? "")
      : (adherentNir ?? "");
  }
  return "";
};

export const childUsesStrictNirValidation = (authority) =>
  authority === AUTHORITY_TYPE.SELF || authority == null;

/**
 * Spouse is not on the contract but the child's NIR is attached to them —
 * collect the parent's NIR manually on the child row.
 */
export const childRequiresOffContractSpouseNir = (
  authority,
  hasSpouse = false,
  nirInheritanceFrom,
) =>
  (authority === AUTHORITY_TYPE.MOTHER && !hasSpouse) ||
  (authority === AUTHORITY_TYPE.BOTH &&
    nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE &&
    !hasSpouse);

/** True when the child's NIR is typed on the child row (strict validation / editable field). */
export const childNirIsManualEntry = (
  authority,
  hasSpouse = false,
  nirInheritanceFrom,
) =>
  childUsesStrictNirValidation(authority) ||
  childRequiresOffContractSpouseNir(authority, hasSpouse, nirInheritanceFrom);

export const parentsHaveDifferentSocialRegimes = ({
  adherentSocialRegime,
  spouseSocialRegime,
  hasSpouse = false,
}) => {
  if (!hasSpouse) return false;
  if (!adherentSocialRegime || !spouseSocialRegime) return false;
  return adherentSocialRegime !== spouseSocialRegime;
};

export const shouldAskChildrenSocialRegime = (params) =>
  parentsHaveDifferentSocialRegimes(params);

export const resolveChildSocialRegimeForPricing = ({
  childSocialRegime,
  adherentSocialRegime,
  spouseSocialRegime,
  hasSpouse = false,
}) => {
  if (childSocialRegime) {
    return childSocialRegime;
  }

  if (
    shouldAskChildrenSocialRegime({
      adherentSocialRegime,
      spouseSocialRegime,
      hasSpouse,
    })
  ) {
    return childSocialRegime;
  }

  return adherentSocialRegime;
};

export const isChildLinkedToAdherent = ({ authority, nirInheritanceFrom }) =>
  authority === AUTHORITY_TYPE.FATHER ||
  (authority === AUTHORITY_TYPE.BOTH &&
    nirInheritanceFrom !== NIR_INHERITANCE_FROM.SPOUSE);

export const isChildLinkedToSpouse = ({
  authority,
  nirInheritanceFrom,
  hasSpouse = false,
}) =>
  hasSpouse &&
  (authority === AUTHORITY_TYPE.MOTHER ||
    (authority === AUTHORITY_TYPE.BOTH &&
      nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE));

export const resolveChildLinkedParentRegime = ({
  authority,
  nirInheritanceFrom,
  adherentSocialRegime,
  spouseSocialRegime,
  hasSpouse = false,
}) => {
  if (!authority || authority === AUTHORITY_TYPE.SELF) {
    return null;
  }
  if (isChildLinkedToAdherent({ authority, nirInheritanceFrom })) {
    return adherentSocialRegime || null;
  }
  if (isChildLinkedToSpouse({ authority, nirInheritanceFrom, hasSpouse })) {
    return spouseSocialRegime || null;
  }
  return null;
};

export const hasChildSocialRegimeAttachmentMismatch = ({
  authority,
  nirInheritanceFrom,
  childSocialRegime,
  adherentSocialRegime,
  spouseSocialRegime,
  hasSpouse = false,
}) => {
  const parentRegime = resolveChildLinkedParentRegime({
    authority,
    nirInheritanceFrom,
    adherentSocialRegime,
    spouseSocialRegime,
    hasSpouse,
  });
  if (!parentRegime || !childSocialRegime) {
    return false;
  }
  return childSocialRegime !== parentRegime;
};

export const childHasSocialRegimeAttachmentMismatch = ({
  child,
  adherentSocialRegime,
  spouseSocialRegime,
  hasSpouse = false,
}) =>
  hasChildSocialRegimeAttachmentMismatch({
    authority: get(child, "authority"),
    nirInheritanceFrom: getNirInheritanceFromStored(child),
    childSocialRegime: get(child, "identity.socialRegime"),
    adherentSocialRegime,
    spouseSocialRegime,
    hasSpouse,
  });
